import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';

/**
 * AI-JAVADOC: DcPageObjectComponent
 *
 * ObjectComponent for dc.DcPage. Exposes three left-navigation sections for the
 * children of a DcPage (Complex Configuration Entity Rule, CONFIG-003):
 *  - Inhalte  -> dc.DcPageContent filtered by e.pageId
 *  - Objekte  -> dc.DcPageObject filtered by e.pageId
 *  - Begriffe -> dc.DcPageTerm filtered by e.pageId
 *
 * Registered via dc.entity-provider.ts for MvsCrudModeEnum.update so that
 * row-click on a DcPage in the config page opens this Object Page instead
 * of the generic CRUD form (CONFIG-015 Step 3).
 */
@Component({
    selector: 'dc-page-object-component',
    templateUrl: 'dc-page-object.component.html',
    standalone: false
})
export class DcPageObjectComponent extends ObjectBaseComponent implements OnInit, OnDestroy {

    contentsWidget: WidgetData;
    objectsWidget: WidgetData;
    termsWidget: WidgetData;

    navigationItems: NavigationItem[] = [
        {
            label: 'Inhalte',
            action: 'contents',
            tooltip: 'Inhalte dieser Seite',
            icon: 'fa-regular fa-file-alt',
            toggleable: false,
            default: true
        },
        {
            label: 'Objekte',
            action: 'objects',
            tooltip: 'Objekte dieser Seite',
            icon: 'fa-regular fa-cube',
            toggleable: false,
            default: false
        },
        {
            label: 'Begriffe',
            action: 'terms',
            tooltip: 'Begriffe dieser Seite',
            icon: 'fa-regular fa-tag',
            toggleable: false,
            default: false
        }
    ];

    private queryParamSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        private route: ActivatedRoute
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit() {
        super.ngOnInit();

        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItem = params['navItem'];
            if (navItem) {
                const found = this.navigationItems.find(item => item.action === navItem);
                if (found) {
                    this.activeNavigationItems = found;
                }
            }
        });
    }

    /**
     * Called when the DcPage dto is loaded or refreshed.
     * Emits navigation items to the Object Page host and creates the three
     * child widgets filtered by the current pageId.
     */
    onObjectChanged() {
        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }
        this.onNavigationItems.emit(this.navigationItems);
        this.createChildWidgets();
    }

    private createChildWidgets(): void {
        const pageId = this.objectIdentifier.objectId;
        const filter = [FilterCriteria.create('e.pageId', FilterCriteria.cOperatorEqual, pageId)];
        const fkFields: WidgetToolbarCreateInterface[] = [{fieldName: 'pageDtoId', fieldValue: pageId}];

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Contents table widget filtered by DcPage id
         */
        this.contentsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.page.object.children.content',
            'Inhalte',
            'dc.DcPageContent',
            filter
        );
        this.contentsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Objects table widget filtered by DcPage id
         */
        this.objectsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.page.object.children.object',
            'Objekte',
            'dc.DcPageObject',
            filter
        );
        this.objectsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Terms table widget filtered by DcPage id
         */
        this.termsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.page.object.children.term',
            'Begriffe',
            'dc.DcPageTerm',
            filter
        );
        this.termsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.queryParamSubscription?.unsubscribe();
    }
}
